  <template>
    <nav>
      <AlertMessages
        v-if="alertValue"
        :alert-type="alertType"
        :alert-msg="alertMsg"
        @reset="alertValue = false"
      />
      <v-row>
        <v-col :cols="mobile() ? '12' : '11'" :class="mobile() ? '' : 'mx-auto'">
          <span class="float-right">
            <v-menu v-if="mobile()" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="px-5 py-2"
                  height="100%"
                  icon
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon large>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
            </v-menu>
          </span>
          <p
            :class="
              mobile()
                ? 'text-h5 font-weight-bold ml-16 mr-16'
                : 'text-h5 font-weight-bold'
            "
            :align="mobile() ? 'center' : 'left'"
          >
            {{ $t("settingsVisma") }}
          </p>
        </v-col>
      </v-row>
      <v-row v-if="!loadingData">
        <v-col :cols="mobile() ? '12' : '11'" :class="mobile() ? '' : 'mx-auto'">
          <v-form ref="form" @submit.prevent>
            <v-card color="background" elevation="0" max-width="700px">
              <v-card-text>
                <p class="text-body-1 darktext--text no-break mb-2 ml-5">
                  {{ $t("labelAppId") }}
                </p>
                <v-text-field
                  v-model="appId"
                  data-testid="app-id-input"
                  solo
                  class="rounded-xxl text-body-2"
                  hide-details="auto"
                  :placeholder="$t('fortnoxAppIdPlaceholder')"
                  :rules="appIdRules"
                />
                <p class="text-body-1 darktext--text no-break mt-4 mb-2 ml-5">
                  {{ $t("labelSecret") }}
                </p>
                <v-text-field
                  v-model="token"
                  data-testid="token-input"
                  solo
                  class="rounded-xxl text-body-2"
                  hide-details="auto"
                  :placeholder="$t('fortnoxSecretPlaceholder')"
                  :rules="secretKeyRules"
                />
              </v-card-text>

              <v-card-actions>
                <v-btn
                  ref="submit"
                  data-testid="submit-button"
                  type="submit"
                  color="accent"
                  min-width="100"
                  dark
                  elevation="0"
                  class="rounded-xxl text-none px-4 mr-2"
                  @click="integrationId ? updateIntegration() : saveIntegration()"
                >
                  {{ $t("save") }}
                </v-btn>
                <v-btn
                  ref="test"
                  data-testid="authorize-button"
                  v-if="integrationId && showAuthorize"
                  color="darktext"
                  min-width="100"
                  dark
                  elevation="0"
                  outlined
                  class="rounded-xxl text-none px-4 mr-2"
                  @click="authorizeConnection()"
                >
                  {{ $t("authorizeConnection") }}
                </v-btn>
                <v-chip
                  v-if="tokenStatus == 'authorized'"
                  color="success"
                  data-testid="chip-order-approved"
                  class="rounded-xxl text-none px-4 white--text mr-2"
                  >{{ $t("connected") }}</v-chip
                >
                <v-chip
                  v-else-if="tokenStatus == 'expired'"
                  color="error"
                  data-testid="chip-order-denied"
                  class="rounded-xxl text-none px-4 white--text mr-2"
                  >{{ $t("expired") }}</v-chip
                >
                <v-chip
                  v-else-if="
                    tokenStatus == 'missing_token' || tokenStatus == 'failed'
                  "
                  color="warning"
                  data-testid="chip-order-pending"
                  class="rounded-xxl text-none px-4 white--text mr-2"
                  >{{ $t("pending") }}</v-chip
                >
                <v-progress-circular
                  v-if="loadingStatus"
                  size="24"
                  indeterminate
                  data-testid="status-loading"
                  class="text-left justify-left ma-8"
                />
              </v-card-actions>
            </v-card>
          </v-form>
        </v-col>
      </v-row>
    </nav>
  </template>

  <script>
  import i18n from "../../plugins/i18n";
  import { repeatedMethodsMixin } from "../../mixins/repeatedMethodsMixin";
  import AlertMessages from "../../components/AlertMessages";
  import { saveIntegration, updateIntegration } from "../../api/spaceApi";
  import {
    authorizeVisma,
    getVismaIntegration,
    searchVismaArticles,
  } from "../../api/vismaApi";
  import Echo from "laravel-echo";
  import { ruleRequired } from "../../rules/formRules.js";

  export default {
    components: {
      AlertMessages,
    },
    mixins: [repeatedMethodsMixin],

    data() {
      return {
        alertMsg: "",
        alertType: "",
        alertValue: false,
        loadingStatus: false,
        tokenStatus: "",
        loadingData: false,
        integrationId: null,
        appId: null,
        token: null,
        spaceId: window.$cookies.get("space_id"),
        appIdRules: ruleRequired("labelAppId"),
        secretKeyRules: ruleRequired("labelSecret"),
        valid: true,
        authToken: null,
        showAuthorize: false,
      };
    },
    computed: {
      productPlan() {
        return this.$store.state.productPlan;
      },
    },
    async created() {
      this.loadingData = true;
      await this.fetchIntegrations();
      this.loadingData = false;
    },
    methods: {
      beforeDestroy() {
        window.Echo.leave("integration-tracker." + this.integrationId);
      },
      showUsersStatus() {
        this.$router.push({ path: "/space/visma-users" });
      },
      showSettings() {
        this.$router.push({ path: "/space/visma-settings" });
      },
      async fetchIntegrations() {
        try {
          const response = await getVismaIntegration(this.spaceId);
          if (response.status) {
            if (response.status == 200) {
              if (response.data.data.length > 0) {
                this.loadingStatus = true;
                this.token = response.data.data.find(
                  (item) => item.attributes.name == "visma"
                ).attributes.token;
                this.appId = response.data.data.find(
                  (item) => item.attributes.name == "visma"
                ).attributes.app_id;
                this.integrationId = response.data.data.find(
                  (item) => item.attributes.name == "visma"
                ).id;
                if (
                  response.data.data[0].meta &&
                  response.data.data[0].meta.status
                ) {
                  this.tokenStatus = response.data.data[0].meta.status;
                  if (this.tokenStatus == "valid") {
                    this.testVismaIntegration();
                  } else {
                    this.showAuthorize = true;
                    this.loadingStatus = false;
                  }
                }
              }
            }
          }
        } catch (e) {
          if (e.response) {
            if (e.response.status == 404) {
              this.$router.push({ name: "PageNotFound" });
            } else if (e.response.status == 403) {
              this.$router.push({ name: "PageAccessDenied" });
            } else {
              this.alertValue = true;
              this.alertType = "error";
              this.alertMsg = i18n.t("serverError");
            }
          }
          this.loadingStatus = false;
        }
      },

      async testVismaIntegration() {
        try {
          const response = await searchVismaArticles(this.integrationId, "");
          if (response.status == 200) {
            this.showAuthorize = false;
            this.tokenStatus = "authorized";
          }
          this.loadingStatus = false;
        } catch (e) {
          if (e.response) {
            this.tokenStatus = "failed";
            this.showAuthorize = true;

            this.alertValue = true;
            this.alertType = "error";
            if (e.response.data.errors) {
              for (let i = 0; i < e.response.data.errors.length; i++) {
                this.alertMsg = e.response.data.errors[i].detail;
              }
            } else if (e.response.status == 403) {
              this.alertMsg = e.response.data.detail;
            }
            this.loadingStatus = false;
          }
        }
      },

      openAuthorizationWindow(url) {
        const w = 600;
        const h = 400;
        const left = screen.width / 2 - w / 2;
        const top = screen.height / 2 - h / 2;

        // Open a new window with the specified dimensions and URL
        window.open(
          url,
          "_blank",
          `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${top}, left=${left}`
        );
      },
      async authorizeConnection() {
        this.valid = this.$refs.form.validate();
        if (this.valid) {
          try {
            const response = await authorizeVisma(this.integrationId);
            if (response.status == 200) {
              window.$cookies.set(
                "vismaId",
                this.integrationId,
                0,
                null,
                process.env.VUE_APP_DOMAIN
              );

              var url = response.data.meta.url;
              this.openAuthorizationWindow(url);
              setTimeout(() => {
                this.listen();
              }, 3000);
            }
          } catch (e) {
            if (e.response) {
              if (e.response.status == 403) {
                for (let i = 0; i < e.response.data.errors.length; i++) {
                  if (e.response.data.errors[i].title == "integration_error") {
                    this.alertValue = true;
                    this.alertType = "error";
                    this.alertMsg = i18n.t("testVismaIntegrationError");
                  } else {
                    this.alertValue = true;
                    this.alertType = "error";
                    this.alertMsg = i18n.t("serverError");
                  }
                }
              } else {
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("serverError");
              }
            }
          }
        }
      },

      async saveIntegration() {
        this.valid = this.$refs.form.validate();
        if (this.valid) {
          try {
            let data = {
              data: {
                type: "integrations",
                attributes: {
                  app_id: this.appId,
                  token: this.token,
                  name: "visma",
                },
                relationships: {
                  space: {
                    data: {
                      type: "spaces",
                      id: this.spaceId,
                    },
                  },
                },
              },
            };
            const response = await saveIntegration(data);
            if (response.status == 201) {
              this.alertValue = true;
              this.alertType = "success";
              this.alertMsg = i18n.t("saveVismaIntegrationSuccess");
              this.integrationId = response.data.data.id;
              this.showAuthorize = true;
            }
          } catch (e) {
            if (e.response) {
              this.alertValue = true;
              this.alertType = "error";
              this.alertMsg = i18n.t("serverError");
            }
          }
        }
      },
      async updateIntegration() {
        this.valid = this.$refs.form.validate();
        if (this.valid) {
          try {
            let data = {
              data: {
                type: "integrations",
                id: this.integrationId,
                attributes: {
                  app_id: this.appId,
                  token: this.token,
                  name: "visma",
                },
                relationships: {
                  space: {
                    data: {
                      type: "spaces",
                      id: this.spaceId,
                    },
                  },
                },
              },
            };
            const response = await updateIntegration(this.integrationId, data);
            if (response.status == 200) {
              this.alertValue = true;
              this.alertType = "success";
              this.alertMsg = i18n.t("saveVismaIntegrationSuccess");
              this.fetchIntegrations();
            }
          } catch (e) {
            if (e.response) {
              this.alertValue = true;
              this.alertType = "error";
              this.alertMsg = i18n.t("serverError");
            }
          }
        }
      },
      async listen() {
        this.loadingStatus = true;
        window.Echo = new Echo({
          broadcaster: "pusher",
          key: process.env.VUE_APP_PUSHER_KEY,
          cluster: "eu",
          encryted: true,
          authEndpoint:
            process.env.VUE_APP_API_BASE_URL + "/v1/broadcasting/auth",
          auth: {
            headers: {
              Accept: "application/vnd.api+json",
              Authorization: "Bearer " + window.$cookies.get("token"),
            },
          },
        });
        window.Echo.private("integration-tracker." + this.integrationId).listen(
          "IntegrationAuthorized",
          () => {
            this.testVismaIntegration();
          }
        );
      },
    },
  };
  </script>
